import {
  makeContentServiceDeleteRequest,
  makeContentServiceGetRequest,
  makeContentServicePostRequest,
} from 'infrastructure/contentService'
import {
  AssetUploadResultsResponse,
  AssetUploadStartResponse,
  AssetUploadStatusResponse,
} from './types'

type ErrorWithStatus = Error & { status?: number }

function parseErrorResponse(response: Response): Promise<never> {
  return response.text().then((rawResponse) => {
    const error = new Error(
      rawResponse || `Request failed with status ${response.status}`,
    ) as ErrorWithStatus
    error.status = response.status
    throw error
  })
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    return parseErrorResponse(response)
  }

  const rawResponse = await response.text()
  try {
    return JSON.parse(rawResponse) as T
  } catch {
    throw new Error(rawResponse)
  }
}

/**
 * Starts the asset upload pipeline for the selected tree and version.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @param includeImages - Whether image URLs should be scanned.
 * @param includeDocuments - Whether document URLs should be scanned.
 * @param includeAudioVideo - Whether audio/video URLs should be scanned.
 * @returns API response body.
 */
export async function startAssetUpload(
  treeId: string,
  versionId: string,
  includeImages: boolean,
  includeDocuments: boolean,
  includeAudioVideo: boolean,
): Promise<AssetUploadStartResponse> {
  const response = await makeContentServicePostRequest(
    `content/v1/asset-upload/${treeId}:${versionId}/start`,
    {
      include_images: includeImages,
      include_documents: includeDocuments,
      include_audio_video: includeAudioVideo,
    },
  )

  return parseJsonResponse<AssetUploadStartResponse>(response)
}

/**
 * Fetches the current pipeline status for the selected tree and version.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @returns Current pipeline status response.
 */
export async function getAssetUploadStatus(
  treeId: string,
  versionId: string,
): Promise<AssetUploadStatusResponse> {
  const response = await makeContentServiceGetRequest(
    `content/v1/asset-upload/${treeId}:${versionId}/status`,
  )

  return parseJsonResponse<AssetUploadStatusResponse>(response)
}

/**
 * Fetches completed asset upload results for the selected tree and version.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @returns Upload result payload.
 */
export async function getAssetUploadResults(
  treeId: string,
  versionId: string,
): Promise<AssetUploadResultsResponse> {
  const response = await makeContentServiceGetRequest(
    `content/v1/asset-upload/${treeId}:${versionId}/results`,
  )

  return parseJsonResponse<AssetUploadResultsResponse>(response)
}

/**
 * Deletes the asset upload pipeline for the selected tree and version.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @returns API response body.
 */
export async function deleteAssetUpload(
  treeId: string,
  versionId: string,
): Promise<Record<string, unknown> | null> {
  const response = await makeContentServiceDeleteRequest(
    `content/v1/asset-upload/${treeId}:${versionId}/delete`,
  )

  return parseJsonResponse<Record<string, unknown> | null>(response)
}
